// drafts-to-evaluations-list.ts (gh-471, Option A) — the PURE offline-read transform for the LIST.
//
// Reconstructs the `EvaluationsListResponse` shape the evaluations list + workspace home render
// from, out of the device-local `drafts` rows, so a COLD OFFLINE boot has a navigable path to the
// in-progress draft (Workspace "In-progress" card → `/evaluations/:id`, which gh-459 then resumes
// from IDB) instead of stranding every list on an infinite spinner. Consumed by
// `useListEvaluations`'s queryFn fallback when the network GET fails with a transport error.
//
// PURE (no Dexie, no React, no network) so it unit-tests directly and can run in the queryFn —
// mirrors `draft-to-evaluation-detail.ts`.
//
// PHI: the projection is already PHI-free — `EvaluationListItem` carries `client_id` (plaintext
// UUID) and NO name/DOB. Reconstructing it from a local row persists no decrypted identity.

import type { DraftRecord } from "./idb-schema-def";
import type {
  EvaluationListItem,
  EvaluationState,
  EvaluationsListResponse,
} from "@/hooks/queries/evaluation-types";

export interface OfflineListParams {
  page: number;
  limit: number;
  /** Canonical states to include; empty ⇒ the server default (all EXCEPT archived). */
  status: EvaluationState[];
  /** Restrict to one client (UUID); undefined ⇒ all of the caller's drafts. */
  clientId: string | undefined;
}

/** Map a local `drafts` row to the list-item projection the screens render. */
function toListItem(row: DraftRecord): EvaluationListItem {
  const currentStep = (row.data as { current_step?: unknown }).current_step;
  return {
    id: row.id,
    // Persisted at CREATE / back-filled on the online GET (gh-459). A legacy pre-fix row without it
    // still renders; the empty string is inert (the row still links to `/evaluations/:id`).
    client_id: row.client_id ?? "",
    // `DraftRecord.state` is only `"draft" | "submitted"` — both are valid `EvaluationState`s.
    state: row.state,
    current_step: typeof currentStep === "string" ? currentStep : null,
    created_at: row.created_at,
    // Match the server contract: `null` for a row never updated since creation. Local rows always
    // stamp `updated_at`, but keep the fallback explicit so the sort/ display never sees `undefined`.
    updated_at: row.updated_at ?? null,
  };
}

/** Most-recent-first: the server orders `updated_at` DESC, so `results[0]` is the latest draft
 * (what the Workspace in-progress card reads). Fall back to `created_at` when `updated_at` is null.
 * ISO 8601 strings are lexicographically ordered, so a plain string compare is correct AND
 * locale-invariant — `localeCompare` would defer to device collation rules that don't apply here. */
function byUpdatedDesc(a: DraftRecord, b: DraftRecord): number {
  const at = a.updated_at ?? a.created_at;
  const bt = b.updated_at ?? b.created_at;
  return bt < at ? -1 : bt > at ? 1 : 0;
}

/**
 * Reconstruct an `EvaluationsListResponse` from local `drafts` rows (offline fallback for
 * `useListEvaluations`). Applies the same filters/order/pagination the server would:
 *
 *  - `clientId` — keep only rows for that client when set.
 *  - `status` — keep rows whose state is in the requested set; an EMPTY set is the server default
 *    (all states except `archived`), and IDB only ever holds `draft`/`submitted` rows, so an empty
 *    set keeps everything. A set that asks ONLY for `scored`/`archived` matches nothing offline
 *    (those terminal states are never persisted as local drafts) → an honest empty list.
 *  - sort `updated_at` DESC, then paginate by `page`/`limit`.
 *
 * `count` is the TOTAL matched (pre-pagination) so KPI tiles read the real number; `next`/`previous`
 * are a truthy NON-URL sentinel when another page exists (the screens read them only as booleans) else
 * `null` — a fake `/evaluations?page=` URL would look fetchable but 404 offline.
 */
export function draftsToEvaluationsList(
  rows: DraftRecord[],
  params: OfflineListParams,
): EvaluationsListResponse {
  const { page, limit, status, clientId } = params;

  const matched = rows
    .filter((row) => (clientId ? row.client_id === clientId : true))
    .filter((row) => (status.length > 0 ? status.includes(row.state) : true))
    .sort(byUpdatedDesc);

  const count = matched.length;
  const start = (page - 1) * limit;
  const results = matched.slice(start, start + limit).map(toListItem);

  const hasNext = start + limit < count;
  const hasPrevious = page > 1 && count > 0;

  return {
    count,
    // Boolean-only at the call sites (`Boolean(data?.next)`); a non-URL sentinel keeps the shape
    // honest without implying a fetchable URL offline (a real path would 404 with no network).
    next: hasNext ? "__offline_next__" : null,
    previous: hasPrevious ? "__offline_prev__" : null,
    results,
  };
}
